// High-score persistence. localStorage-only — no backend, no accounts.
// Top-N list sorted by survival time, tie-broken by kills then breeds.
// Pure(-ish) module: takes an injected Storage so it's testable headless.

export interface HighScore {
  /** Survival time in seconds. */
  time: number;
  kills: number;
  breeds: number;
  /** Best emergent trait ids discovered this run (deduped). */
  bestEmergent: string[];
  /** ISO timestamp of when the run ended. */
  date: string;
}

export const STORAGE_KEY = 'ball-breeder:high-scores';
export const MAX_SCORES = 15;

/** Read scores from storage. Returns [] on missing / corrupt data. */
export function loadScores(storage: Storage = localStorage): HighScore[] {
  const raw = storage.getItem(STORAGE_KEY);
  if (!raw) return [];
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(isHighScore);
  } catch {
    return [];
  }
}

/** Save scores back. */
export function saveScores(scores: HighScore[], storage: Storage = localStorage): void {
  storage.setItem(STORAGE_KEY, JSON.stringify(scores));
}

/** Insert a new score, sort, clamp to MAX_SCORES, persist, return updated list. */
export function recordScore(score: HighScore, storage: Storage = localStorage): HighScore[] {
  const all = [...loadScores(storage), score];
  const sorted = sortScores(all).slice(0, MAX_SCORES);
  saveScores(sorted, storage);
  return sorted;
}

/** True iff this score would make the top-N. Used by the GameOver UI to
 *  decide whether to celebrate. */
export function isHighScore_(score: HighScore, storage: Storage = localStorage): boolean {
  const all = loadScores(storage);
  if (all.length < MAX_SCORES) return true;
  const sorted = sortScores(all);
  const worst = sorted[sorted.length - 1]!;
  return compareScores(score, worst) < 0;
}

/** Sort: higher time first; ties broken by kills, then breeds. */
export function sortScores(scores: readonly HighScore[]): HighScore[] {
  return [...scores].sort(compareScores);
}

function compareScores(a: HighScore, b: HighScore): number {
  if (a.time !== b.time) return b.time - a.time;
  if (a.kills !== b.kills) return b.kills - a.kills;
  return b.breeds - a.breeds;
}

function isHighScore(x: unknown): x is HighScore {
  if (typeof x !== 'object' || x === null) return false;
  const o = x as Record<string, unknown>;
  return (
    typeof o.time === 'number' &&
    typeof o.kills === 'number' &&
    typeof o.breeds === 'number' &&
    Array.isArray(o.bestEmergent) &&
    typeof o.date === 'string'
  );
}
